import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { ASSETS } from "../lib/constants";
import LanguageSwitcher from "./LanguageSwitcher";
import { Button } from "./ui/button";

export default function Header({ onBookNow }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/rentals", label: t("nav.rentals") },
    { to: "/gallery", label: t("nav.gallery", "Gallery") },
    { to: "/about", label: t("nav.about") },
    { to: "/mission", label: t("nav.mission") },
    { to: "/find-us", label: t("nav.findus") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="main-nav"
      className="sticky top-0 z-40 w-full border-b border-white/10 bg-brand-black/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-10">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3">
          <img src={ASSETS.logo} alt="Cascante Moto Rental" className="h-12 w-auto" />
          <div className="hidden flex-col leading-none sm:flex">
            <span className="font-display text-2xl tracking-wide text-white">CASCANTE</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand-orange">
              Moto Rental · SJDS
            </span>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `relative px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${
                  isActive ? "text-brand-orange" : "text-white hover:text-brand-orange"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <Button
            data-testid="header-book-now-btn"
            onClick={onBookNow}
            className="rounded-none bg-brand-orange px-5 py-5 font-display text-lg uppercase tracking-wider text-white hover:bg-brand-orange-dark"
          >
            {t("nav.bookNow")}
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-1" data-testid="mobile-menu-btn">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="p-2 text-white"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-brand-black lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm font-bold uppercase tracking-widest border-l-2 ${
                    isActive ? "border-brand-orange text-brand-orange" : "border-transparent text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center justify-end gap-3 pt-3">
              <Button
                data-testid="mobile-book-now-btn"
                onClick={() => {
                  setOpen(false);
                  onBookNow?.();
                }}
                className="rounded-none bg-brand-orange font-display text-lg uppercase tracking-wider text-white hover:bg-brand-orange-dark"
              >
                {t("nav.bookNow")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
